(() => {
  'use strict';

  const MEDIA_SELECTOR = '[data-dv-deal-media], [data-dv-quality-media]';
  const VIDEO_SELECTOR = ':scope > video.dv-daily-deal__video, :scope > video.dv-quality-deal__video';
  const TEMPLATE_SELECTOR = '[data-dv-deal-video-template], [data-dv-quality-video-template]';
  const PLAY_SELECTOR = '[data-dv-deal-video-play]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 768px)');
  const connectedVideos = new Set();
  const visibleMedia = new Set();

  function saveDataEnabled() {
    return Boolean(navigator.connection && navigator.connection.saveData);
  }

  function videoFor(media) {
    return media.querySelector(VIDEO_SELECTOR);
  }

  function playButtonFor(media) {
    return media.querySelector(PLAY_SELECTOR);
  }

  function setPlayButtonVisible(media, visible) {
    const button = playButtonFor(media);
    if (!button) return;
    button.hidden = !visible;
    button.setAttribute('aria-pressed', visible ? 'false' : 'true');
  }

  function activate(media) {
    let video = videoFor(media);
    if (video) return video;
    const template = media.querySelector(TEMPLATE_SELECTOR);
    if (!template) return null;
    const fragment = template.content.cloneNode(true);
    video = fragment.querySelector('video');
    if (!video) return null;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'none';
    video.setAttribute('aria-hidden', 'true');
    video.setAttribute('tabindex', '-1');
    template.after(fragment);
    connectedVideos.add(video);

    video.addEventListener('playing', () => {
      media.classList.add('is-playing');
    });
    video.addEventListener('pause', () => {
      media.classList.remove('is-playing');
    });
    video.addEventListener('ended', () => {
      media.classList.remove('is-playing');
    });
    return video;
  }

  function pause(video) {
    if (video && !video.paused) video.pause();
  }

  function deactivate(video) {
    if (!video) return;
    pause(video);
    const media = video.closest(MEDIA_SELECTOR);
    if (media) {
      media.classList.remove('is-playing', 'is-video-active');
      setPlayButtonVisible(media, true);
    }
    connectedVideos.delete(video);
    video.remove();
  }

  function deactivateAll(exceptVideo) {
    Array.from(connectedVideos).forEach((video) => {
      if (video !== exceptVideo) deactivate(video);
    });
  }

  function canAutoplay() {
    return desktop.matches &&
      !saveDataEnabled() &&
      !reducedMotion.matches &&
      !document.hidden;
  }

  function playDesktop(media) {
    if (!canAutoplay()) return;
    const video = activate(media);
    if (!video) return;
    deactivateAll(video);
    video.controls = false;
    media.classList.remove('is-video-active');
    setPlayButtonVisible(media, false);
    if (video.ended) video.currentTime = 0;
    const result = video.play();
    if (result && typeof result.catch === 'function') {
      result.catch(() => {
        media.classList.remove('is-playing');
        setPlayButtonVisible(media, true);
      });
    }
  }

  function playOnRequest(media) {
    if (document.hidden) return;
    const video = activate(media);
    if (!video) return;
    deactivateAll(video);
    video.controls = true;
    media.classList.add('is-video-active');
    setPlayButtonVisible(media, false);
    if (video.ended) video.currentTime = 0;
    const result = video.play();
    if (result && typeof result.catch === 'function') {
      result.catch(() => deactivate(video));
    }
  }

  function connect() {
    const mediaItems = Array.from(document.querySelectorAll(MEDIA_SELECTOR));
    if (!mediaItems.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const media = entry.target;
        const video = videoFor(media);
        if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
          visibleMedia.add(media);
          playDesktop(media);
        } else {
          visibleMedia.delete(media);
          deactivate(video);
        }
      });
    }, { threshold: [0, 0.55, 0.8] });

    mediaItems.forEach((media) => observer.observe(media));

    document.addEventListener('click', (event) => {
      const button = event.target.closest(PLAY_SELECTOR);
      if (!button) return;
      const media = button.closest(MEDIA_SELECTOR);
      if (!media) return;
      event.preventDefault();
      playOnRequest(media);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        connectedVideos.forEach(pause);
      } else {
        visibleMedia.forEach(playDesktop);
      }
    });

    reducedMotion.addEventListener?.('change', () => {
      if (reducedMotion.matches) {
        Array.from(connectedVideos).forEach(deactivate);
      } else {
        visibleMedia.forEach(playDesktop);
      }
    });

    desktop.addEventListener?.('change', () => {
      Array.from(connectedVideos).forEach(deactivate);
      if (desktop.matches) visibleMedia.forEach(playDesktop);
    });

    window.addEventListener('pagehide', () => {
      Array.from(connectedVideos).forEach(deactivate);
      visibleMedia.clear();
    }, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', connect, { once: true });
  } else {
    connect();
  }
})();
